// Single reducer for every server-pushed group/room/knock event, so the UI
// never has to reconcile scattered useState calls against an event stream.
// On reconnect the client re-runs presence:sync and GROUP_SNAPSHOT fully
// replaces that group's room list rather than merging - the server is the
// only source of truth for who's where.

export const initialGroupState = {
    groups: [],              // [{groupId, name, ownerId, memberCount, role}]
    roomsByGroup: {},        // groupId -> [{roomId, type, name, doorState, capacity, ownerId, availability, occupants}]
    currentRoomId: null,     // room this client is physically in, if any
    voiceToken: null,
    musicState: null,        // {roomId, status, nowPlaying, queue, loop, errorMessage, updatedAt} | null - the music bot's state for currentRoomId, same flat single-room shape as voiceToken
    videoWatchState: null,   // {roomId, status, video, positionSec, updatedAt} | null - synced "watch together" state for currentRoomId, same flat shape/lifecycle as musicState but fully independent of the bot
    transcriptState: null,   // {roomId, status, startedAt, startedBy, recentSegments, updatedAt} | null - same flat single-room shape/lifecycle as musicState, for the transcription bot (Backend/services/transcriptBot/)
    transcriptPartials: {},  // userId -> {username, text} - live, not-yet-finalized speech per speaker. Reset on every TRANSCRIPT_STATE_CHANGED (a session boundary invalidates any in-flight utterance) and per-speaker on their next TRANSCRIPT_SEGMENT.
    pendingOutgoingKnock: null, // {knockId, roomId, expiresAt}
    incomingKnocks: [],      // [{knockId, roomId, from, expiresAt}]
    lastKnockResult: null,   // {status, roomId} - transient, for a toast
    lastRemovedGroupId: null // groupId this client was just removed from by the owner - transient, for Groups.js to drop it locally
};

function updateRoomEverywhere(state, roomId, updater) {
    const roomsByGroup = { ...state.roomsByGroup };
    let changed = false;
    Object.keys(roomsByGroup).forEach((groupId) => {
        const idx = roomsByGroup[groupId].findIndex((r) => r.roomId === roomId);
        if (idx === -1) return;
        const rooms = roomsByGroup[groupId].slice();
        rooms[idx] = updater(rooms[idx]);
        roomsByGroup[groupId] = rooms;
        changed = true;
    });
    return changed ? { ...state, roomsByGroup } : state;
}

export function groupReducer(state, action) {
    switch (action.type) {
        case 'GROUPS_LOADED':
            return { ...state, groups: action.groups };

        case 'GROUP_SNAPSHOT':
            return { ...state, roomsByGroup: { ...state.roomsByGroup, [action.groupId]: action.rooms } };

        case 'OCCUPANT_JOINED':
            return updateRoomEverywhere(state, action.roomId, (room) => {
                if (!action.user || room.occupants.some((o) => o.userId === action.user.userId)) return room;
                return { ...room, occupants: [...room.occupants, action.user] };
            });

        case 'OCCUPANT_LEFT':
            return updateRoomEverywhere(state, action.roomId, (room) => ({
                ...room,
                occupants: room.occupants.filter((o) => o.userId !== action.userId)
            }));

        case 'AVAILABILITY_CHANGED':
            return updateRoomEverywhere(state, action.roomId, (room) => ({ ...room, availability: action.availability }));

        case 'DOOR_STATE_CHANGED':
            return updateRoomEverywhere(state, action.roomId, (room) => ({ ...room, doorState: action.doorState }));

        case 'ROOM_CLOSED':
            return state.currentRoomId === action.roomId ? { ...state, currentRoomId: null, voiceToken: null, musicState: null, videoWatchState: null, transcriptState: null, transcriptPartials: {} } : state;

        case 'YOU_WERE_KICKED':
            return state.currentRoomId === action.roomId ? { ...state, currentRoomId: null, voiceToken: null, musicState: null, videoWatchState: null, transcriptState: null, transcriptPartials: {} } : state;

        case 'ENTERED_ROOM': {
            // occupants is only known when this comes from room:join's ack -
            // knock:accepted only carries a voiceToken. When absent, the
            // occupant list catches up naturally: the newly-entered user's
            // own socket is already in the room channel by the time the
            // server broadcasts room:occupantJoined, so it fires for self too.
            //
            // Always resets musicState/videoWatchState/transcriptState too,
            // not just currentRoomId/voiceToken - joinRoom() leaves the old
            // room first (LEFT_ROOM already clears these) before joining a
            // new one, but the knock-accept paths in SocketContext.js
            // (knock:accepted, respondKnock's own accept branch) dispatch
            // ENTERED_ROOM directly with no LEFT_ROOM in between, since
            // there's no explicit "leave" step when a knock pulls you into a
            // different room. Without this reset, the room you were just in
            // left its videoWatchState/musicState behind, e.g. keeping
            // VideoWatchOverlay stuck open showing a watch-party/stream that
            // belonged to a room you're no longer even in. The caller's own
            // *_STATE_CHANGED dispatches right after this one (see
            // knock:accepted, room:resynced, room:join's ack) repopulate
            // these correctly when the new room actually has an active
            // session, and correctly leave them null when it doesn't.
            const next = {
                ...state,
                currentRoomId: action.roomId,
                voiceToken: action.voiceToken,
                musicState: null,
                videoWatchState: null,
                transcriptState: null,
                transcriptPartials: {}
            };
            if (!action.occupants) return next;
            next.roomsByGroup = Object.fromEntries(Object.entries(state.roomsByGroup).map(([groupId, rooms]) => [
                groupId,
                rooms.map((r) => (r.roomId === action.roomId ? { ...r, occupants: action.occupants } : r))
            ]));
            return next;
        }

        case 'LEFT_ROOM':
            return { ...state, currentRoomId: null, voiceToken: null, musicState: null, videoWatchState: null, transcriptState: null, transcriptPartials: {} };

        case 'VOICE_TOKEN_REFRESHED':
            return { ...state, voiceToken: action.voiceToken };

        // Dispatched separately from ENTERED_ROOM (not baked into it) so
        // this case stays single-purpose like the rest of the file - fired
        // by the server's music:stateChanged broadcast, or handed over
        // explicitly on room:join/room:resynced/knock:accepted's ack/payload
        // so a client sees an already-in-progress bot session immediately
        // instead of waiting for the next broadcast. Ignored if it's not for
        // the room we're currently in (a late event arriving just after
        // leaving, or a stale one from before a reconnect).
        case 'MUSIC_STATE_CHANGED':
            return action.musicState.roomId === state.currentRoomId ? { ...state, musicState: action.musicState } : state;

        // Same shape/rationale as MUSIC_STATE_CHANGED just above, for the
        // independent "watch together" video feature - see videoWatchSession.js
        // (Backend/services/videoWatch/) for the server-side state machine.
        case 'VIDEO_WATCH_STATE_CHANGED':
            return action.videoWatchState.roomId === state.currentRoomId ? { ...state, videoWatchState: action.videoWatchState } : state;

        // Same shape/rationale as MUSIC_STATE_CHANGED, for the transcription
        // bot - see transcriptSession.js (Backend/services/transcriptBot/).
        // A new session (start()) always begins with an empty
        // recentSegments/status:'connecting', so replacing wholesale here is
        // correct - it's meant to reset the panel, not merge with whatever
        // the previous session left behind. transcriptPartials resets too:
        // any in-flight (not yet finalized) utterance belongs to whichever
        // session was running when it started, and a state change always
        // marks a session boundary (a fresh start OR a stop).
        case 'TRANSCRIPT_STATE_CHANGED':
            return action.transcriptState.roomId === state.currentRoomId
                ? { ...state, transcriptState: action.transcriptState, transcriptPartials: {} }
                : state;

        // One finalized utterance. Appended to the LIVE list the panel
        // renders from (transcriptState.recentSegments) - capped well past
        // the server's own RECENT_SEGMENTS_LIMIT (50) so a client that's
        // been sitting in an active session for a while keeps more scrollback
        // than what a late joiner's initial snapshot carries. Deduped by seq
        // in case a segment somehow arrives twice (e.g. a brief reconnect
        // replaying a buffered event). Clears that speaker's partial - their
        // in-flight utterance just became this final line.
        case 'TRANSCRIPT_SEGMENT': {
            if (!state.transcriptState || action.roomId !== state.currentRoomId) return state;
            if (state.transcriptState.recentSegments.some((s) => s.seq === action.seq)) return state;
            const recentSegments = [...state.transcriptState.recentSegments, action].slice(-200);
            const transcriptPartials = { ...state.transcriptPartials };
            delete transcriptPartials[action.userId];
            return { ...state, transcriptState: { ...state.transcriptState, recentSegments }, transcriptPartials };
        }

        // Live, throttled, not-yet-finalized speech - see PARTIAL_THROTTLE_MS
        // in transcriptSession.js. Keyed by speaker so simultaneous speakers
        // each get their own line instead of clobbering one shared one.
        case 'TRANSCRIPT_PARTIAL':
            if (action.roomId !== state.currentRoomId) return state;
            return { ...state, transcriptPartials: { ...state.transcriptPartials, [action.userId]: { username: action.username, text: action.text } } };

        case 'KNOCK_SENT':
            return { ...state, pendingOutgoingKnock: { knockId: action.knockId, roomId: action.roomId, expiresAt: action.expiresAt } };

        case 'KNOCK_RECEIVED':
            return { ...state, incomingKnocks: [...state.incomingKnocks, action.knock] };

        case 'KNOCK_RESOLVED': {
            const next = {
                ...state,
                incomingKnocks: state.incomingKnocks.filter((k) => k.knockId !== action.knockId)
            };
            if (state.pendingOutgoingKnock && state.pendingOutgoingKnock.knockId === action.knockId) {
                next.pendingOutgoingKnock = null;
                next.lastKnockResult = { status: action.status, roomId: action.roomId };
            }
            return next;
        }

        case 'CLEAR_LAST_KNOCK_RESULT':
            return { ...state, lastKnockResult: null };

        case 'MEMBER_JOINED':
            if (!state.roomsByGroup[action.groupId]) return state;
            return {
                ...state,
                roomsByGroup: {
                    ...state.roomsByGroup,
                    [action.groupId]: [...state.roomsByGroup[action.groupId], { ...action.room, availability: 'available', occupants: [] }]
                }
            };

        case 'MEMBER_LEFT':
            if (!state.roomsByGroup[action.groupId]) return state;
            return {
                ...state,
                roomsByGroup: {
                    ...state.roomsByGroup,
                    [action.groupId]: state.roomsByGroup[action.groupId]
                        .filter((r) => !(r.type === 'personal' && r.ownerId === action.userId))
                        .map((r) => ({ ...r, occupants: r.occupants.filter((o) => o.userId !== action.userId) }))
                }
            };

        case 'GROUP_MEMBERSHIP_REMOVED': {
            const roomsByGroup = { ...state.roomsByGroup };
            const inThisGroup = (roomsByGroup[action.groupId] || []).some((r) => r.roomId === state.currentRoomId);
            delete roomsByGroup[action.groupId];
            const next = { ...state, roomsByGroup, lastRemovedGroupId: action.groupId };
            // The group went away under us while we were in one of its rooms.
            return inThisGroup
                ? { ...next, currentRoomId: null, voiceToken: null, musicState: null, videoWatchState: null, transcriptState: null, transcriptPartials: {} }
                : next;
        }

        default:
            return state;
    }
}
